import { NumberField } from '@base-ui/react/number-field';
import { t } from '@lingui/core/macro';
import type { ComponentPropsWithoutRef } from 'react';

import ChevronDown from '@/assets/svg/chevron-down.svg?react';
import { cn } from '@/lib/utils';

import { useNumberInputContext } from './NumberInputContext';

const triggerButtonSizeClasses = {
  small: cn('h-3 w-5'),
  medium: cn('h-4 w-5'),
  large: cn('size-5'),
} satisfies Record<string, string>;

const triggerButtonIconSizeClasses = {
  small: cn('[&_svg]:size-3'),
  medium: cn('[&_svg]:size-4'),
  large: cn('[&_svg]:size-4.5'),
} satisfies Record<string, string>;

export type NumberInputTriggerProps = ComponentPropsWithoutRef<'div'>;

const NumberInputTrigger = ({ className, ...props }: NumberInputTriggerProps) => {
  const { size } = useNumberInputContext();

  const triggerButtonClassName = cn(
    'flex cursor-pointer items-center justify-center rounded-none bg-transparent text-gray-900',
    'select-none hover:bg-gray-100 active:bg-gray-100',
    'disabled:cursor-not-allowed disabled:text-gray-400',
    'data-disabled:cursor-not-allowed data-disabled:text-gray-400',
    triggerButtonSizeClasses[size],
    triggerButtonIconSizeClasses[size],
  );

  return (
    <div
      data-slot="number-input-trigger"
      className={cn(
        'pointer-events-none absolute inset-y-0 right-0 z-1 flex items-center pr-0 opacity-0 transition-opacity duration-400 ease-in-out',
        'group-hover/number-input:pointer-events-auto group-hover/number-input:opacity-100',
        'group-focus-within/number-input:pointer-events-auto group-focus-within/number-input:opacity-100',
        className,
      )}
      {...props}
    >
      <div className="flex h-full flex-col justify-center">
        <NumberField.Increment
          className={triggerButtonClassName}
          aria-label={t`Increase value`}
          tabIndex={-1}
        >
          <ChevronDown className="rotate-180" />
        </NumberField.Increment>
        <NumberField.Decrement
          className={triggerButtonClassName}
          aria-label={t`Decrease value`}
          tabIndex={-1}
        >
          <ChevronDown />
        </NumberField.Decrement>
      </div>
    </div>
  );
};

NumberInputTrigger.displayName = 'NumberInputTrigger';

export default NumberInputTrigger;
